import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentAdmin } from "@/hooks/useCurrentAdmin";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, RefreshCcw, RotateCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDateString } from "@/lib/utils";

interface FailedRow {
  id: string;
  document_name: string;
  document_type: string;
  una_id: string | null;
  una_name: string | null;
  filed_at: string | null;
  error: string | null;
}

interface StewLogFailureRow {
  id: string;
  kind: string;
  contact_id: string | null;
  session_id: string | null;
  error: string | null;
  created_at: string;
}

export default function AdminStewMirror() {
  const { admin, loading: adminLoading, isSuperAdmin } = useCurrentAdmin();
  const { toast } = useToast();
  const [rows, setRows] = useState<FailedRow[]>([]);
  const [logRows, setLogRows] = useState<StewLogFailureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(true);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryingLog, setRetryingLog] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // No foreign key exists between generated_documents.una_id and unas, so a
    // PostgREST embed here fails the whole request. Read the names separately.
    const { data, error } = await (supabase as any)
      .from("generated_documents")
      .select("id, document_name, document_type, una_id, merge_data")
      .filter("merge_data->_stew_filing->mirror->>status", "eq", "failed")
      .order("updated_at", { ascending: false })
      .limit(200);
    if (error) {
      setLoading(false);
      console.error("AdminStewMirror load failed:", error);
      toast({
        title: "Failed mirror list didn't load",
        description: "Refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    const unaIds = Array.from(
      new Set(((data as any[]) || []).map((r) => r.una_id).filter(Boolean)),
    );
    const nameById = new Map<string, string>();
    if (unaIds.length > 0) {
      const { data: unaRows } = await (supabase as any)
        .from("unas")
        .select("id, name")
        .in("id", unaIds);
      ((unaRows as any[]) || []).forEach((u) => nameById.set(u.id, u.name));
    }
    setLoading(false);
    if (error) {
      console.error("AdminStewMirror load failed:", error);
      toast({
        title: "Failed mirror list didn't load",
        description: "Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    setRows(
      (data || []).map((r: any) => ({
        id: r.id,
        document_name: r.document_name,
        document_type: r.document_type,
        una_id: r.una_id,
        una_name: (r.una_id && nameById.get(r.una_id)) || null,
        filed_at: r?.merge_data?._stew_filing?.filed_at || null,
        error: r?.merge_data?._stew_filing?.mirror?.error || null,
      })),
    );
  }, [toast]);

  const loadLogFailures = useCallback(async () => {
    setLogLoading(true);
    const { data, error } = await (supabase as any)
      .from("public_stew_log_failures")
      .select("id, kind, contact_id, session_id, error, created_at")
      .is("resolved_at", null)
      .order("created_at", { ascending: false })
      .limit(200);
    setLogLoading(false);
    if (error) {
      console.error("AdminStewMirror log failures load failed:", error);
      toast({
        title: "Public Stew failure list didn't load",
        description: "Refresh and try again.",
        variant: "destructive",
      });
      return;
    }
    setLogRows(data || []);
  }, [toast]);

  useEffect(() => {
    if (!adminLoading && admin) {
      void load();
      void loadLogFailures();
    }
  }, [adminLoading, admin, load, loadLogFailures]);

  const retry = useCallback(
    async (id: string) => {
      setRetrying(id);
      try {
        const { data, error } = await supabase.functions.invoke(
          "stew-file-document",
          { body: { documentId: id } },
        );
        if (error) throw error;
        const status = (data as any)?.mirror_status;
        toast({
          title:
            status === "delivered"
              ? "Mirror delivered"
              : status === "skipped"
                ? "Mirror not configured"
                : "Mirror still failing",
          description:
            status === "delivered"
              ? "The backup copy was accepted by the remote store."
              : status === "skipped"
                ? "Backup delivery is not configured yet. The operator can enable it in project settings."
                : "Retry did not succeed. Check the error and try again later.",
          variant: status === "failed" ? "destructive" : "default",
        });
        await load();
      } catch (e: any) {
        console.error("AdminStewMirror retry failed:", e);
        toast({
          title: "Retry didn't run",
          description: "Try again in a moment.",
          variant: "destructive",
        });
      } finally {
        setRetrying(null);
      }
    },
    [load, toast],
  );

  const retryLog = useCallback(
    async (id: string) => {
      setRetryingLog(id);
      try {
        const { data, error } = await supabase.functions.invoke(
          "retry-stew-log-failure",
          { body: { failureId: id } },
        );
        if (error) throw error;
        const ok = (data as any)?.ok;
        toast({
          title: ok ? "Ontraport call succeeded" : "Retry still failing",
          description: ok
            ? "The failure has been marked resolved."
            : ((data as any)?.error || "Ontraport rejected the retry. Check the error and try again later."),
          variant: ok ? "default" : "destructive",
        });
        await loadLogFailures();
      } catch (e: any) {
        console.error("AdminStewMirror retry log failed:", e);
        toast({
          title: "Retry didn't run",
          description: "Try again in a moment.",
          variant: "destructive",
        });
      } finally {
        setRetryingLog(null);
      }
    },
    [loadLogFailures, toast],
  );

  if (adminLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Stew Delivery Failures</CardTitle>
            <CardDescription>
              Stew-filed documents whose backup copy did not reach secure
              storage. Retry sends the same payload again.
              {isSuperAdmin() ? "" : " (Read plus retry only.)"}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCcw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No failed mirrors. Everything is delivered or skipped.
            </p>
          ) : (
            <div className="divide-y">
              {rows.map((r) => (
                <div
                  key={r.id}
                  className="py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {r.document_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {r.una_name || r.una_id || "Unknown Association"} ·{" "}
                      {r.document_type} ·{" "}
                      {r.filed_at ? formatDateString(r.filed_at) : "—"}
                    </div>
                    {r.error && (
                      <div className="text-xs text-destructive mt-1 truncate">
                        {r.error}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void retry(r.id)}
                    disabled={retrying === r.id}
                  >
                    {retrying === r.id ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Retry
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Public Stew Ontraport Failures</CardTitle>
            <CardDescription>
              Note or tag calls to Ontraport from Public Stew that did not
              succeed after one automatic retry. Retry re-runs the recorded
              request.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadLogFailures()}
            disabled={logLoading}
          >
            <RefreshCcw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {logLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading
            </div>
          ) : logRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No unresolved Public Stew failures.
            </p>
          ) : (
            <div className="divide-y">
              {logRows.map((r) => (
                <div
                  key={r.id}
                  className="py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {r.kind} · contact {r.contact_id || "unknown"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {formatDateString(r.created_at)}
                    </div>
                    {r.error && (
                      <div className="text-xs text-destructive mt-1 truncate">
                        {r.error}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void retryLog(r.id)}
                    disabled={retryingLog === r.id}
                  >
                    {retryingLog === r.id ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Retry
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
